import {CurrentUserService} from "../../services/user/CurrentUserService";
import {WatchlistModel} from "../models/WatchlistModel";
import {QuoteViewModel, QuoteViewModels} from "../../quote/models/QuoteViewModel";
import {CurrentUser} from "../../services/user/models/CurrentUser";
import {Comparator, Transform} from "../../types/GenericTypes";
import {CodeAndExchange, encodeSecurity} from "../../instruments/types/CodeAndExchange";
import {FeatureNames} from "../../types/UserConfig";
import {
    orderWatchListItems,
    orderWatchListItemsByChangePct,
    orderWatchListItemsByCode,
    WatchlistItemViewModel,
    WatchlistItemViewModels,
    WatchlistViewModel
} from "../models/WatchlistViewModel";
import {PriceStrategyService} from "../../components/inputs/price/service/PriceStrategyService";
import {AsxIndexUtil} from "../../js/util/asx/AsxIndexUtil";
import {SecurityMap} from "../../security/common/map/SecurityMap";
import {NewsHeadlineIndicatorModel} from "../../news/models/NewsHeadlineIndicatorModel";
import {buildQuoteRequest} from "../../quote/util/QuoteStoreUtil";
import {QuoteModelFactory} from "../../quote/service/QuoteModelFactory";
import {missingQuote} from "../../quote/models/QuoteRecord";
import {WatchlistSortColumn, WatchlistSorting} from "../models/WatchlistSort";
import {reverse} from "../../js/util/UtilFunctions";
import {SortingDirection} from "../../components/ui/sort-indicator/SortingDirection";

export class WatchListViewModelFactory {

    public static readonly id = "watchListViewModelFactory";

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly priceStrategyService: PriceStrategyService,
                       private readonly quoteModelFactory: QuoteModelFactory) {
    }

    public async buildViewModel(watchlist: WatchlistModel,
                                quotes: QuoteViewModels,
                                headlines: SecurityMap<NewsHeadlineIndicatorModel>,
                                sorting: WatchlistSorting): Promise<WatchlistViewModel> {
        return this.currentUserService
                   .current
                   .first()
                   .map((user) => {
                       const items = watchlist.securities.map(this.buildItem(quotes, headlines, user));
                       const sortedItems = this.orderItems(items, sorting);
                       return WatchlistViewModel.create(watchlist, sortedItems, sorting);
                   }).toPromise();
    }

    private orderItems(items: WatchlistItemViewModels, sorting: WatchlistSorting): WatchlistItemViewModels {
        return [...items].sort(this.toComparator(sorting));
    }

    private toComparator(sortOptions: WatchlistSorting): Comparator<WatchlistItemViewModel> {
        if (sortOptions == null) {
            return orderWatchListItems();
        }

        switch (sortOptions.direction) {
            case SortingDirection.ASCENDING:
                return this.comparatorForColumn(sortOptions.column);
            case SortingDirection.DESCENDING:
                return reverse(this.comparatorForColumn(sortOptions.column));
        }
    }

    private comparatorForColumn(column: WatchlistSortColumn): Comparator<WatchlistItemViewModel> {
        switch (column) {
            case WatchlistSortColumn.CHANGE:
                return orderWatchListItemsByChangePct();
            case WatchlistSortColumn.CODE:
                return orderWatchListItemsByCode();
        }
    }

    private buildItem(quotes: QuoteViewModels,
                      headlines: SecurityMap<NewsHeadlineIndicatorModel>,
                      user: CurrentUser): Transform<CodeAndExchange, WatchlistItemViewModel> {
        return (security) => {
            let quote = quotes.find((q) => (q.code === security.code) && (q.exchange === security.exchange));
            quote = quote ?? this.buildMissingQuote(security);
            const headline = headlines.get(security);
            const link = this.calculateLink(security, user);
            const strategy = this.priceStrategyService.forCode(security.code);
            return WatchlistItemViewModel.create(security, strategy, link, quote, headline);
        };
    }

    private calculateLink(security: CodeAndExchange, user: CurrentUser): string | null {
        if (!AsxIndexUtil.isIndex(security.code) && user.hasFeature(FeatureNames.SCREEN_COMPANY_INFO)) {
            const encoded = encodeSecurity(security);
            return `company-info({security: "${encoded}"})`;
        } else if (user.hasFeature(FeatureNames.FEATURE_ADV_CHARTING)) {
            return `charting({symbol: "${security.code}"})`;
        } else if (user.hasFeature(FeatureNames.SCREEN_STRATEGY_BUILDER)) {
            return `strategy({underlying: "${security.code}"})`;
        } else {
            return null;
        }
    }

    private buildMissingQuote(security: CodeAndExchange): QuoteViewModel {
        const request = buildQuoteRequest(security);
        const quote = missingQuote(request);
        return this.quoteModelFactory.buildViewModel(quote);
    }
}
